import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet, Navigate, useLocation } from "react-router-dom";
import { ReactLenis } from "lenis/react";
import { AnimatePresence, motion, MotionConfig, useReducedMotion, useScroll, useSpring } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import MobileActionBar from "@/components/MobileActionBar";
import CookieConsent from "@/components/CookieConsent";
import WhatsAppBubble from "@/components/WhatsAppBubble";

import Home from "@/pages/Home";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import HowWeWork from "@/pages/HowWeWork";
import About from "@/pages/About";
import Areas from "@/pages/Areas";
import LocationPage from "@/pages/LocationPage";
import Insights from "@/pages/Insights";
import ArticleDetail from "@/pages/ArticleDetail";
import Contact from "@/pages/Contact";
import Consultation from "@/pages/Consultation";
import ConsultationThankYou from "@/pages/ConsultationThankYou";
import Calculator from "@/pages/Calculator";
import Legal from "@/pages/Legal";
import NotFound from "@/pages/NotFound";

const AdminLogin = lazy(() => import("@/admin/AdminLogin"));
const AdminApp = lazy(() => import("@/admin/AdminApp"));

// Legacy project URLs from the pre-2026 portfolio → nearest genuine project.
const LEGACY_PROJECT_REDIRECTS = {
  "axbridge-rear-extension-renovation": "glastonbury-extension-renovation",
  "bath-contemporary-rear-extension": "bath-modern-rear-extension",
  "contemporary-rear-extension-bath": "bath-modern-rear-extension",

  "avonmouth-rear-extension-renovation": "bristol-city-full-renovation-extension",
  "thornbury-loft-garage-annex": "thornbury-rear-extension-kitchen",
  "gwent-wraparound-extension": "yeovil-wraparound-extension",
  "taunton-deane-rear-extension": "taunton-rear-extension-open-plan-kitchen",
  "yate-rear-extension": "emersons-green-rear-extension",
  "rear-extension-contemporary-family-living-space-in-yate": "emersons-green-rear-extension",
  "newport-luxury-renovation-extension": "clifton-luxury-renovation-extension",
  "clifton-rear-extension-kitchen": "clifton-luxury-renovation-extension",
  "bristol-city-centre-luxury-renovation-orangery": "clifton-luxury-renovation-extension",
  "bridgwater-extension-renovation": "bristol-city-full-renovation-extension",
  "west-monkton-extension-kitchen": "taunton-rear-extension-open-plan-kitchen",
  "exeter-city-kitchen-extension": "bath-kitchen-extension",
  "ottery-st-mary-listed-renovation-extension": "bath-victorian-rear-extension",
  "branscombe-listed-farmhouse-renovation": "bath-victorian-rear-extension",
  "contemporary-extension-full-home-renovation": "bristol-city-full-renovation-extension",
};

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

const isPrerenderHydration = () =>
  typeof window !== "undefined" && window.__NOOR_PRERENDERED__ === true;

function ScrollProgress() {
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 140, damping: 30, mass: 0.25 });
  if (reduceMotion) return null;
  // Prerender capture scrolls the page to trigger reveals — pin the bar to 0
  // so the snapshot matches the hydration render (page loads at scroll 0),
  // otherwise React throws hydration error #418.
  const capturing = typeof window !== "undefined" && window.__NOOR_PRERENDER_CAPTURE__ === true;
  return (
    <motion.div
      data-testid="scroll-progress"
      aria-hidden="true"
      className="fixed top-0 left-0 right-0 h-[2px] bg-ember z-[70] origin-left pointer-events-none"
      style={{ scaleX: capturing ? 0 : scaleX }}
    />
  );
}

// Casual-copy deterrents on public pages only (admin keeps full control):
// no right-click/drag on photos & video, no Ctrl/Cmd+S or Ctrl/Cmd+P.
function ContentProtection() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname.startsWith("/admin")) return undefined;
    const blockMediaContext = (e) => {
      if (e.target.closest?.("img, video, picture")) e.preventDefault();
    };
    const blockMediaDrag = (e) => {
      if (e.target.closest?.("img, video")) e.preventDefault();
    };
    const blockSaveKeys = (e) => {
      if ((e.ctrlKey || e.metaKey) && ["s", "p"].includes(e.key.toLowerCase())) e.preventDefault();
    };
    document.addEventListener("contextmenu", blockMediaContext);
    document.addEventListener("dragstart", blockMediaDrag);
    document.addEventListener("keydown", blockSaveKeys);
    return () => {
      document.removeEventListener("contextmenu", blockMediaContext);
      document.removeEventListener("dragstart", blockMediaDrag);
      document.removeEventListener("keydown", blockSaveKeys);
    };
  }, [pathname]);
  return null;
}

function SiteLayout() {
  return (
    <>
      <ScrollProgress />
      <Navbar />
      <main>
        <Outlet />
      </main>
      <Footer />
      <div className="h-[58px] md:hidden" aria-hidden="true" />
      <MobileActionBar />
      <CookieConsent />
      <WhatsAppBubble />
    </>
  );
}

function AppRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={isPrerenderHydration() ? false : { opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      >
        <Suspense fallback={<div className="min-h-[50vh] bg-cream" />}>
          <Routes location={location}>
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<Suspense fallback={<div className="min-h-screen bg-graphite" />}><AdminApp /></Suspense>} />
          <Route element={<SiteLayout />}>
            <Route index element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/projects/:slug" element={<ProjectDetail />} />
            <Route path="/how-we-work" element={<HowWeWork />} />
            <Route path="/about" element={<About />} />
            <Route path="/areas-we-cover" element={<Areas />} />
            <Route path="/areas-we-cover/:slug" element={<LocationPage />} />
            <Route path="/insights" element={<Insights />} />
            <Route path="/insights/:slug" element={<ArticleDetail />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/request-a-consultation" element={<Consultation />} />
            <Route path="/request-a-consultation/thank-you" element={<ConsultationThankYou />} />
            <Route path="/cost-calculator" element={<Calculator />} />
            <Route path="/privacy-policy" element={<Legal key="privacy-policy" slug="privacy-policy" />} />
            <Route path="/cookie-policy" element={<Legal key="cookie-policy" slug="cookie-policy" />} />
            <Route path="/terms-of-use" element={<Legal key="terms-of-use" slug="terms-of-use" />} />
            <Route path="/testimonials" element={<Navigate to="/" replace />} />
            <Route path="/reviews" element={<Navigate to="/" replace />} />
            <Route path="/blog" element={<Navigate to="/insights" replace />} />
            {/* Consolidated Devon town pages → the county page (in-app redirect;
                hosting cannot issue true HTTP 301s — do not describe as such) */}
            {["builders-plymouth", "builders-torquay-paignton", "builders-newton-abbot-teignmouth-dawlish",
              "builders-barnstaple-bideford", "builders-tavistock-totnes"].map((s) => (
              <Route key={s} path={`/areas-we-cover/${s}`} element={<Navigate to="/areas-we-cover/builders-devon" replace />} />
            ))}
            {Object.entries(LEGACY_PROJECT_REDIRECTS).map(([from, to]) => (
              <Route key={from} path={`/projects/${from}`} element={<Navigate to={`/projects/${to}`} replace />} />
            ))}
            <Route path="*" element={<NotFound />} />
          </Route>
          </Routes>
        </Suspense>
      </motion.div>
    </AnimatePresence>
  );
}

function App() {
  return (
    <MotionConfig reducedMotion="user">
      <ReactLenis root options={{ lerp: 0.1, wheelMultiplier: 1 }}>
        <BrowserRouter>
          <ScrollToTop />
          <ContentProtection />
          <AppRoutes />
          <Toaster position="top-center" />
        </BrowserRouter>
      </ReactLenis>
    </MotionConfig>
  );
}

export default App;
